/**
 * Swiss hreflang management for proper multilingual SEO
 * Implements Swiss regional codes and reciprocal alternate links
 */

import { SwissLanguage, languageToRegion } from './swissI18n';

interface HreflangLink {
  hreflang: string;
  href: string;
}

interface HreflangProps {
  currentLanguage: SwissLanguage;
  currentPath: string;
  availableLanguages: SwissLanguage[];
}

/**
 * Generate hreflang alternate links for Swiss regional codes
 */
export const generateHreflangLinks = ({
  currentLanguage,
  currentPath,
  availableLanguages
}: HreflangProps): HreflangLink[] => {
  const baseUrl = 'https://www.future-level.ch';
  const links: HreflangLink[] = [];

  // Add links for each available language with Swiss regional codes
  availableLanguages.forEach(language => {
    const regionalCode = languageToRegion[language];
    const href = `${baseUrl}/${language}${currentPath}`;
    
    links.push({
      hreflang: regionalCode,
      href
    });
  });

  // Add x-default pointing to German (primary market)
  links.push({
    hreflang: 'x-default',
    href: `${baseUrl}/de${currentPath}`
  });

  return links;
};

/**
 * Update hreflang links in document head
 */
export const updateHreflangLinks = (links: HreflangLink[]): void => {
  // Remove existing hreflang links
  const existingLinks = document.querySelectorAll('link[rel="alternate"][hreflang]');
  existingLinks.forEach(link => link.remove());

  // Add new hreflang links
  links.forEach(({ hreflang, href }) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    link.href = href;
    document.head.appendChild(link);
  });
};

/**
 * Generate sitemap URLs for all languages
 */
export const generateSitemapUrls = (
  basePath: string,
  availableLanguages: SwissLanguage[]
): string[] => {
  const baseUrl = 'https://www.future-level.ch';
  return availableLanguages.map(language => 
    `${baseUrl}/${language}${basePath}`
  );
};

/**
 * Validate hreflang reciprocity (for testing/debugging)
 */
export const validateHreflangReciprocity = (
  currentUrl: string,
  links: HreflangLink[]
): boolean => {
  // Each language should have a corresponding return link
  // This is a simplified check - in production you'd crawl each URL
  return links.length >= 2; // At minimum current language + x-default
};